import {
  IonButton,
  IonButtons,
  IonText,
  IonToolbar,
} from '@ionic/react';
import React, {
  useMemo, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ROUTES_PATH } from '@/constants';
import ENVIRONMENT from '@/environments/environment';
import useModal from '@/hooks/useModal';
import { ClientTaxonomyEvents } from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import { ErrorsInterface } from '@/interfaces/ErrorInterface/ErrorInterface';
import { checkIsLogged, saveExpiredCredentials, savePreviousPassword } from '@/redux/auth/auth';
import { postLeads, postToken } from '@/redux/auth/authAPI';
import { TokenRequest } from '@/redux/auth/authInterface';
import { addError } from '@/redux/error/error';
import { Analytics } from '@/services/analytics';
import { setBiometricCredentials, typeOfBiometryAvailable } from '@/services/biometry';
import InsightService from '@/services/insights';
import { storageService } from '@/services/storage';
import { loginTrack } from '@/services/tracking';
import { isPasswordExpiredError, isWeakPassword, PASSWORD_LENGTH } from '@/utils/password';

import PinInputs from '../PinInputs/PinInputs';
import SetupBiometryModal from '../SetupBiometryModal';
import { SetupBiometryModalProps } from '../SetupBiometryModal/SetupBiometryModal';
import styles from './LoginKeys.module.scss';

interface LoginKeysProps {
  username: string;
  resentPassword: () => void;
  /** `unexpected`: error no previsto, el aviso no culpa a los datos introducidos */
  loginError: (unexpected?: boolean) => void;
  biometryUsernameMismatch: boolean;
  onBiometryEnabled?: () => void;
  biometryAutoAccept?: boolean;
  redirectOnSuccess?: boolean;
}

export default function LoginKeys({
  username,
  resentPassword,
  loginError,
  biometryUsernameMismatch,
  onBiometryEnabled,
  biometryAutoAccept = false,
  redirectOnSuccess = true,
}: LoginKeysProps) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [password, setPassword] = useState<string>('');

  const { presentModal: presentSetupBiometryModal } = useModal(SetupBiometryModal);
  const biometryType = useMemo(() => storageService.getBiometrySaved(), []);
  const previousUsernameMismatch = useRef<boolean>(false);

  const handleBiometry = async () => {
    const biometryAvailable = await typeOfBiometryAvailable();
    if (biometryAvailable !== undefined && previousUsernameMismatch.current) {
      presentSetupBiometryModal<SetupBiometryModalProps>({
        biometryType: biometryAvailable,
        onDismiss: (configure) => {
          if (configure) {
            activateBiometry();
          } else {
            dispatch(checkIsLogged());
          }
        },
      });
    } else if ((biometryUsernameMismatch && biometryAvailable !== undefined)
      || (biometryType === null && biometryAvailable !== undefined)) {
      if (biometryAutoAccept) {
        activateBiometry();
      } else {
        presentSetupBiometryModal<SetupBiometryModalProps>({
          biometryType: biometryAvailable,
          onDismiss: (configure) => {
            if (configure) {
              activateBiometry();
            } else {
              dispatch(checkIsLogged());
            }
          },
        });
      }
    } else {
      dispatch(checkIsLogged());
    }
  };

  const activateBiometry = async () => {
    const success = await setBiometricCredentials(username, password);
    if (success && onBiometryEnabled) {
      onBiometryEnabled();
    }
    // Ya no hay paso intermedio para reintentar: se entra aunque falle el guardado
    if (redirectOnSuccess) {
      dispatch(checkIsLogged());
    }
  };

  const getLead = async () => {
    try {
      const leadsResponse = await postLeads();
      sessionStorage.setItem('leadToken', leadsResponse?.data.token);
      const expirationDate = new Date();
      expirationDate.setSeconds(
        expirationDate.getSeconds() + leadsResponse?.data.expires_in,
      );
      sessionStorage.setItem('expiresLeads', expirationDate.toUTCString());
    } catch (err) {
      dispatch(addError(''));
    }
  };
  
  // Demasiados intentos de login: el back responde 429 en vez de USR0003
  const isTooManyAttemptsError = (error: any) => (
    error?.result?.http_code === 429 || error?.response?.status === 429
  );

  const handleLogin = async (tokenData: TokenRequest) => {
    try {
      const tokenResponse = await postToken(tokenData);
      if (tokenResponse) {
        Analytics.registerAnalyticsEvent({ [ClientTaxonomyEvents.LOGIN]: {} });
        InsightService.logTrace('Login', [
          { Login: tokenResponse.result.info },
        ]);
      }
      const { access_token, refresh_token } = tokenResponse.data;
      const expirationDate = new Date(
        new Date().setHours(new Date().getHours() + 2),
      ).toString();
      if (localStorage.getItem('username') !== tokenData.username.toUpperCase()) {
        previousUsernameMismatch.current = true;
      }
      localStorage.setItem('username', tokenData.username.toUpperCase());
      sessionStorage.setItem('expirationDate', expirationDate);
      sessionStorage.setItem('accessToken', access_token);
      sessionStorage.setItem('refreshToken', refresh_token);
      if (ENVIRONMENT.ENV && access_token) {
        loginTrack(tokenData.username.toUpperCase());
      }
      if (access_token) {
        getLead();
      }
      // Clave sin los requisitos de seguridad: se le obliga a cambiarla y no se ofrece
      // la biometría (guardaría una clave que va a cambiar). En BiometryPinModal el
      // usuario ya está dentro, así que no aplica
      if (redirectOnSuccess && isWeakPassword(tokenData.password)) {
        storageService.setMustChangePassword();
        // Para que el cambio no admita la misma clave con la que acaba de entrar
        dispatch(savePreviousPassword(tokenData.password));
        dispatch(checkIsLogged());
        return;
      }
      handleBiometry();
    } catch (error: any) {
      console.log('error', error);
      // Hay que actualizar la clave antes de entrar. En BiometryPinModal el usuario ya
      // está dentro, así que no aplica
      if (redirectOnSuccess && isPasswordExpiredError(error)) {
        dispatch(saveExpiredCredentials({ username: tokenData.username, password: tokenData.password }));
        history.push(ROUTES_PATH.LOGIN_CHANGE_PASSWORD);
        return;
      }
      // Sin red o con una respuesta inesperada el error puede no traer `result`
      if (error?.result?.errors?.find((e: ErrorsInterface) => e.code === 'USR0003')
        || isTooManyAttemptsError(error)
      ) {
        loginError();
      } else {
        loginError(true);
      }
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (username && password.length === PASSWORD_LENGTH) {
      await handleLogin({
        grant_type: 'password', username, password, realm: 'app_merchant',
      });
    }
  };

  return (
    <form className={styles.loginKeysWrapper} onSubmit={handleSubmit}>
      <PinInputs
        id="login-password-input"
        value={password}
        onChange={setPassword}
        onEnter={() => handleSubmit()}
        autoFocus
      />
      <div className={styles.recover}>
        <div className={styles.recoverPassword}>
          <IonText className={styles.forgotPassword}>No recuerdo</IonText>
          <IonText className={styles.forgotPasswordMyKey} onClick={resentPassword}>
            mi clave
          </IonText>
        </div>
      </div>

      <div>
        <IonToolbar className={styles.toolbarButtonsConfirm}>
          <IonButtons className={styles.buttonsConfirm}>
            <IonButton
              className={styles.buttonsPasswordConfirm}
              type="submit"
              disabled={password.length !== PASSWORD_LENGTH}
            >
              Confirmar
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </div>
    </form>
  );
}
